import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { fetchCurrentUser } from '../../store/authSlice';
import industryService from '../../services/industryService';
import logoMain from '../../assets/logo_main.png';
import { Alert, Button, Checkbox, Modal, Spinner } from '../ui';

const IndustrySelectionModal = () => {
    const dispatch = useAppDispatch();
    const { user, isAuthenticated, isLoading: authIsLoading } = useAppSelector((state) => state.auth);

    const [isOpen, setIsOpen] = useState(false);
    const [industries, setIndustries] = useState([]);
    const [selectedIndustries, setSelectedIndustries] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);

    const loadIndustries = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const fetchIndustries = await industryService.getPosIndustries({ is_active: true });
            setIndustries(fetchIndustries);
        } catch (err) {
            console.error("Failed to fetch industries:", err);
            setError("Failed to load industries. Please try again later.");
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        // Only show if authenticated, user data exists, the user-profile load is finished,
        // and no industry is set. Important: don't treat `undefined` as "no industry" yet,
        // otherwise the modal can briefly flash before fetchCurrentUser populates user details.
        const industryCode = user?.pos_industry_code;
        const industryIsMissing = industryCode === null || industryCode === '';

        if (authIsLoading) {
            setIsOpen(false);
            return;
        }

        if (isAuthenticated && user && industryIsMissing) {
            setIsOpen(true);
            loadIndustries();
        } else {
            setIsOpen(false);
        }
    }, [isAuthenticated, user, authIsLoading, loadIndustries]);

    const handleToggleIndustry = (industryCode) => {
        setSelectedIndustries(prev => {
            if (prev.includes(industryCode)) {
                return prev.filter(code => code !== industryCode);
            } else {
                return [...prev, industryCode];
            }
        });
    };

    const handleSave = async () => {
        if (selectedIndustries.length === 0) return;

        setIsSaving(true);
        setError(null);
        try {
            await industryService.setUserIndustry(selectedIndustries);
            // Re-fetch user to get the newly set pos_industry_code
            // The modal will automatically close when the user state updates
            await dispatch(fetchCurrentUser());
        } catch (err) {
            console.error("Failed to set user industry:", err);
            setError(err?.response?.data?.message || err.message || "An error occurred while saving your industry.");
            setIsSaving(false); // Only set to false on error, success will unmount/close
        }
    };

    return (
        <Modal
            open={isOpen}
            onClose={() => {}}
            disableClose
            maxWidth="xs"
            className="max-w-[500px] overflow-hidden bg-gradient-to-br from-white to-slate-50"
            bodyClassName="p-0"
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: -20 }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                className="relative"
            >
                <div
                    className="pointer-events-none absolute -right-[100px] -top-[100px] z-0 h-[300px] w-[300px] rounded-full"
                    style={{ background: 'radial-gradient(circle, rgba(46,158,46,0.1) 0%, transparent 70%)' }}
                />

                <div className="relative z-10 p-8 sm:p-10">
                    <div className="mb-8 text-center">
                        <img src={logoMain} alt="Savanna POS" className="mx-auto mb-6 h-12" />
                        <h2 className="mb-2 text-3xl font-extrabold tracking-tight text-[#212B36]">
                            Select Your Industry
                        </h2>
                        <p className="text-base text-[#637381]">
                            Please help us tailor your experience by selecting your business industry.
                        </p>
                    </div>

                    <AnimatePresence mode="wait">
                        {isLoading ? (
                            <motion.div
                                key="loading"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="flex justify-center py-12"
                            >
                                <Spinner size={40} />
                            </motion.div>
                        ) : error && industries.length === 0 ? (
                            <motion.div
                                key="error"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                            >
                                <Alert severity="error" className="mb-6">
                                    {error}
                                </Alert>
                                <Button
                                    variant="outline"
                                    fullWidth
                                    onClick={loadIndustries}
                                >
                                    Retry
                                </Button>
                            </motion.div>
                        ) : (
                            <motion.div
                                key="content"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 }}
                            >
                                {error && (
                                    <Alert severity="error" className="mb-6">
                                        {error}
                                    </Alert>
                                )}

                                <div className="mb-8 flex flex-col gap-4">
                                    {industries.map((ind) => {
                                        const industryCode = ind.industry_code || ind.name;
                                        const isSelected = selectedIndustries.includes(industryCode);

                                        return (
                                            <motion.div
                                                whileHover={{ scale: 1.01 }}
                                                whileTap={{ scale: 0.99 }}
                                                key={industryCode}
                                                className={`relative flex cursor-pointer items-center rounded-lg border p-4 transition-all duration-200 ${
                                                    isSelected
                                                        ? 'border-[#2e9e2e] bg-[#2e9e2e]/[0.04]'
                                                        : 'border-[#E9EDF6] bg-transparent'
                                                }`}
                                                onClick={() => handleToggleIndustry(industryCode)}
                                            >
                                                <div className="pointer-events-none flex w-full items-center">
                                                    <Checkbox checked={isSelected} onChange={() => {}} className="m-2" />
                                                    <div className="ml-2">
                                                        <p className="text-base font-semibold text-[#212B36]">
                                                            {ind.industry_name || ind.industry_code || ind.name}
                                                        </p>
                                                        {ind.description && (
                                                            <p className="text-sm text-[#637381]">
                                                                {ind.description}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>

                                <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
                                    <Button
                                        fullWidth
                                        size="lg"
                                        onClick={handleSave}
                                        disabled={selectedIndustries.length === 0 || isSaving}
                                        loading={isSaving}
                                        className="rounded-lg text-base font-bold shadow-[0_8px_16px_rgba(46,158,46,0.25)] hover:shadow-[0_12px_20px_rgba(46,158,46,0.35)]"
                                    >
                                        {isSaving ? '' : 'Continue'}
                                    </Button>
                                </motion.div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </motion.div>
        </Modal>
    );
};

export default IndustrySelectionModal;
